import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type CampusRow = { id: string; code: string; name: string; is_active?: number | boolean };

type SchoolSettings = {
  company: {
    id: string;
    name: string;
    code: string;
    legal_name?: string | null;
    address_line1?: string | null;
    address_line2?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    country?: string | null;
    legal_registration_number?: string | null;
  };
  education_profile: Record<string, unknown>;
  campuses: CampusRow[];
  profile_configured: boolean;
};

type ApiError = { response?: { status?: number; data?: { message?: string; error?: string } } };

type ProfileForm = {
  dav_school_code?: string;
  cbse_affiliation_number?: string;
  cbse_school_code?: string;
  board?: string;
  academic_year_label?: string;
  medium_of_instruction?: string;
  principal_name?: string;
  school_phone?: string;
  school_email?: string;
};

function str(value: unknown): string {
  return value == null ? '' : String(value);
}

export default function EducationSettingsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canEdit = Boolean(user?.permissions?.includes('education.admin'));
  const [form] = Form.useForm<ProfileForm>();

  const query = useQuery({
    queryKey: ['education-settings'],
    queryFn: async () => (await api.get<SchoolSettings>('/education/settings')).data,
  });

  const data = query.data;
  const profile = data?.education_profile || {};
  const company = data?.company;
  const error = query.error as ApiError | null;
  const errorStatus = error?.response?.status;
  const errorMessage =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    (errorStatus ? t('unavailable', { status: errorStatus }) : t('settingsLoadFailed'));

  useEffect(() => {
    if (!data) return;
    form.setFieldsValue({
      dav_school_code: str(profile.dav_school_code),
      cbse_affiliation_number: str(
        profile.cbse_affiliation_number || company?.legal_registration_number || ''
      ),
      cbse_school_code: str(profile.cbse_school_code),
      board: str(profile.board || 'CBSE'),
      academic_year_label: str(profile.academic_year_label),
      medium_of_instruction: str(profile.medium_of_instruction),
      principal_name: str(profile.principal_name),
      school_phone: str(profile.school_phone),
      school_email: str(profile.school_email),
    });
  }, [data, form, profile, company?.legal_registration_number]);

  const save = useMutation({
    mutationFn: (values: ProfileForm) => api.put<SchoolSettings>('/education/settings', values),
    onSuccess: (result) => {
      queryClient.setQueryData(['education-settings'], result.data);
      queryClient.invalidateQueries({ queryKey: ['education-home-company'] });
      message.success(t('settingsSaved'));
    },
    onError: (err: ApiError) => {
      message.error(
        err.response?.data?.message ||
          t('unavailable', { status: err.response?.status ?? '' })
      );
    },
  });

  const address = company
    ? [
        company.address_line1,
        company.address_line2,
        company.city,
        company.state,
        company.postal_code,
        company.country,
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  return (
    <EducationPageChrome
      title={t('settingsTitle')}
      description={t('settingsRegisterIntro')}
      tags={
        <>
          <Tag color={data?.profile_configured ? 'green' : 'orange'}>
            {data?.profile_configured ? t('profileConfigured') : t('profileIncomplete')}
          </Tag>
          <Tag>
            {(data?.campuses ?? []).length} {t('campuses')}
          </Tag>
        </>
      }
      extra={
        <Button onClick={() => navigate('/education')}>{t('backToHub', { defaultValue: 'Back to Education' })}</Button>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {query.isError ? (
          <Alert
            type="error"
            showIcon
            message={errorMessage}
            description={
              errorStatus === 403
                ? t('settingsForbiddenHint')
                : errorStatus === 503
                  ? t('settingsDisabledHint')
                  : undefined
            }
          />
        ) : null}

        <Card size="small" title={t('companyIdentity')} loading={query.isLoading}>
          {company ? (
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label={t('schoolName')}>{company.name}</Descriptions.Item>
              <Descriptions.Item label={t('companyCode')}>{company.code}</Descriptions.Item>
              <Descriptions.Item label={t('legalName')}>
                {company.legal_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('address')}>{address || '—'}</Descriptions.Item>
            </Descriptions>
          ) : !query.isError ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('settingsEmpty')} />
          ) : null}
        </Card>

        <Card size="small" title={t('cbseSchoolProfile')}>
          <Form
            form={form}
            layout="vertical"
            disabled={!canEdit || query.isError}
            onFinish={(values) => save.mutate(values)}
          >
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item name="board" label={t('board')}>
                  <Input placeholder="CBSE" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="dav_school_code" label={t('davSchoolCode')}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="cbse_school_code" label={t('cbseSchoolCode')}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="cbse_affiliation_number" label={t('cbseAffiliation')}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="academic_year_label" label={t('academicYearLabel')}>
                  <Input placeholder="2026-27" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="medium_of_instruction" label={t('mediumOfInstruction')}>
                  <Input placeholder="English" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="principal_name" label={t('principalName')}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="school_phone" label={t('schoolPhone')}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="school_email" label={t('schoolEmail')}>
                  <Input type="email" />
                </Form.Item>
              </Col>
            </Row>
            {canEdit ? (
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={save.isPending}>
                {t('save')}
              </Button>
            ) : (
              <Alert type="info" showIcon message={t('settingsReadOnlyHint')} />
            )}
          </Form>
        </Card>

        <Card size="small" title={t('campuses')}>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            loading={query.isLoading}
            dataSource={data?.campuses ?? []}
            locale={{ emptyText: t('campusesEmpty') }}
            columns={[
              { title: t('campusCodeLabel'), dataIndex: 'code', width: 140 },
              { title: t('campusNameLabel'), dataIndex: 'name' },
              {
                title: t('status'),
                width: 100,
                render: (_v, row) => (
                  <Tag color={row.is_active ? 'green' : 'default'}>
                    {row.is_active ? t('active') : t('inactive')}
                  </Tag>
                ),
              },
            ]}
          />
          <Button style={{ marginTop: 12 }} onClick={() => navigate('/education/students')}>
            {t('openStudents')}
          </Button>
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
